import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Lock, ArrowRight, Tag, Landmark, Minus, Plus, Bitcoin } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { useCart, DURATION_LABELS } from "@/context/CartContext";
import { api, apiError } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import CurrencySelect from "@/components/CurrencySelect";
import { toast } from "@/components/ui/sonner";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const SHOW_PAYPAL = false; // PayPal hidden for now — set true to re-enable

export default function CartDrawer() {
  const { items, removeItem, setQty, total, isOpen, closeCart } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("void_email") || "");
  const [discordUser, setDiscordUser] = useState(() => localStorage.getItem("void_discord") || "");
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);

  // bulk tiers (boost lines) vs coupon: the bigger discount wins per line — no stacking
  const bulkPct = (i) => {
    if (i.product.kind !== "boost") return 0;
    const bt = i.product.bulk_tiers;
    const tiers = Array.isArray(bt) ? bt : (bt && bt[i.duration]) || [];
    return tiers.reduce((best, t) => ((i.qty || 1) >= (t.min_qty || 0) ? Math.max(best, t.percent || 0) : best), 0);
  };
  const couponPct = (i) => {
    if (!coupon) return 0;
    if (coupon.product_id && coupon.product_id !== i.product.id) return 0;
    return coupon.percent;
  };
  const lineGross = (i) => i.price * (i.qty || 1);
  const bulkSavings = items.reduce((s, i) => (bulkPct(i) > couponPct(i) ? s + (lineGross(i) * bulkPct(i)) / 100 : s), 0);
  const couponSavings = items.reduce((s, i) => (couponPct(i) >= bulkPct(i) ? s + (lineGross(i) * couponPct(i)) / 100 : s), 0);
  const payable = Math.max(0, total - bulkSavings - couponSavings);
  const { currency, fmt } = useCurrency();

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  // Discord account linking (required once staff configure the Discord integration)
  const [discordRequired, setDiscordRequired] = useState(false);
  const [discordLink, setDiscordLink] = useState(() => {
    try { return JSON.parse(localStorage.getItem("desync_discord_link")); } catch { return null; }
  });
  const [cryptoCoins, setCryptoCoins] = useState([]);
  const [coin, setCoin] = useState("");
  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setDiscordRequired(!!data.discord_link_required);
      const coins = data.crypto_coins || [];
      setCryptoCoins(coins);
      setCoin((c) => c || (coins[0]?.id ?? ""));
    }).catch(() => {});
  }, []);
  const discordReady = !discordRequired || !!discordLink;

  const startDiscordLink = async () => {
    try {
      const { data } = await api.post("/discord/link-url", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const unlinkDiscord = () => {
    localStorage.removeItem("desync_discord_link");
    setDiscordLink(null);
  };

  const cartPayload = () => ({
    email,
    discord_username: discordLink?.name || discordUser.trim(),
    discord_link_token: discordLink?.token || null,
    items: items.map((i) => ({ product_id: i.product.id, duration: i.duration, qty: i.qty || 1 })),
    coupon: coupon ? coupon.code : null,
    currency,
  });

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim() });
      setCoupon(data);
      const targeted = data.product_id && !items.some((i) => i.product.id === data.product_id);
      toast.success(
        targeted
          ? `${data.code} applied — it discounts its product once it's in your cart`
          : `${data.code} applied — ${data.percent}% off`
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChecking(false);
    }
  };

  const checkout = async () => {
    if (!validEmail) {
      toast.error("Enter a valid email — your keys are delivered there");
      return;
    }
    if (discordRequired && !discordLink) {
      toast.error("Link your Discord account first — your customer role unlocks automatically");
      return;
    }
    if (!discordRequired && !discordUser.trim()) {
      toast.error("Enter your Discord username");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms of Service first");
      return;
    }
    if (items.length === 0) return;
    setLoading(true);
    try {
      localStorage.setItem("void_email", email);
      const { data } = await api.post("/payments/checkout", {
        ...cartPayload(),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  const bankCheckout = async () => {
    if (!validEmail) {
      toast.error("Enter a valid email — your keys are delivered there");
      return;
    }
    if (discordRequired && !discordLink) {
      toast.error("Link your Discord account first — your customer role unlocks automatically");
      return;
    }
    if (!discordRequired && !discordUser.trim()) {
      toast.error("Enter your Discord username");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms of Service first");
      return;
    }
    if (items.length === 0) return;
    setLoading(true);
    try {
      localStorage.setItem("void_email", email);
      const { data } = await api.post("/payments/bank-transfer", {
        ...cartPayload(),
        origin_url: window.location.origin,
      });
      navigate(`/payment/bank-pending?order=${data.order_id}`);
      closeCart();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const cryptoCheckout = async () => {
    if (!validEmail) {
      toast.error("Enter a valid email — your keys are delivered there");
      return;
    }
    if (discordRequired && !discordLink) {
      toast.error("Link your Discord account first — your customer role unlocks automatically");
      return;
    }
    if (!discordRequired && !discordUser.trim()) {
      toast.error("Enter your Discord username");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to the Terms of Service first");
      return;
    }
    if (items.length === 0) return;
    setLoading(true);
    try {
      localStorage.setItem("void_email", email);
      const { data } = await api.post("/payments/crypto", {
        ...cartPayload(),
        coin,
        origin_url: window.location.origin,
      });
      closeCart();
      navigate(`/payment/crypto?order=${data.order_id}`);
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#0A1628] border-l border-[#1E2D4A] text-slate-100 overflow-y-auto"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-xl font-bold tracking-tight text-white">
            Your Cart
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-3 pr-1">
          {items.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-16" data-testid="cart-empty-state">
              Your cart is empty
            </div>
          )}
          {items.map((item, idx) => (
            <div
              key={`${item.product.id}-${item.duration}`}
              data-testid={`cart-item-${item.product.id}`}
              className="flex items-center gap-3 p-3 bg-[#050B18] border border-[#1E2D4A] rounded-lg"
            >
              <img src={item.product.image_url} alt="" className="w-14 h-14 object-cover rounded-md" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-white">{item.product.name}</div>
                <div className="text-xs text-slate-500">
                  {item.product.game} · {item.duration_label || DURATION_LABELS[item.duration] || item.duration}
                  {(item.qty || 1) > 1 && <span className="text-[#8FB8E8]"> × {item.qty}</span>}
                </div>
                {item.product.kind === "boost" ? (
                  <div className="flex items-center gap-1.5 mt-1.5" data-testid={`qty-stepper-${item.product.id}`}>
                    <input
                      type="number"
                      value={item.qty || 1}
                      min={item.min_units || 1}
                      step="250"
                      onChange={(e) => setQty(idx, e.target.value)}
                      data-testid={`qty-input-${item.product.id}`}
                      className="w-28 bg-[#0A1628] border border-[#1E2D4A] rounded px-2 py-1 font-mono text-xs text-slate-200 focus:outline-none focus:border-pink-400/60"
                    />
                    <span className="text-[10px] text-slate-600">min {(item.min_units || 1).toLocaleString()}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mt-1.5" data-testid={`qty-stepper-${item.product.id}`}>
                    <button
                      onClick={() => setQty(idx, (item.qty || 1) - 1)}
                      disabled={(item.qty || 1) <= Math.max(1, item.product.min_buy || 1)}
                      data-testid={`qty-minus-${item.product.id}`}
                      className="w-5 h-5 inline-flex items-center justify-center rounded border border-[#1E2D4A] text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="font-mono text-xs text-slate-300 w-6 text-center" data-testid={`qty-value-${item.product.id}`}>{item.qty || 1}</span>
                    <button
                      onClick={() => setQty(idx, (item.qty || 1) + 1)}
                      data-testid={`qty-plus-${item.product.id}`}
                      className="w-5 h-5 inline-flex items-center justify-center rounded border border-[#1E2D4A] text-slate-400 hover:text-white transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-bold text-[#8FB8E8]">
                  {fmt(lineGross(item) * (1 - Math.max(bulkPct(item), couponPct(item)) / 100))}
                </div>
                {bulkPct(item) > 0 && (
                  <div className="text-[10px] font-mono text-emerald-300" data-testid={`bulk-tag-${item.product.id}`}>
                    bulk −{bulkPct(item)}%
                  </div>
                )}
              </div>
              <button
                onClick={() => removeItem(idx)}
                data-testid={`cart-remove-${item.product.id}`}
                className="text-slate-600 hover:text-rose-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-[#1E2D4A] pt-4 mt-4 space-y-4">
          <div>
            <label className="text-sm text-slate-300 block mb-2">Discount code</label>
            {coupon ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-400/10 border border-emerald-400/30" data-testid="cart-coupon-applied">
                <span className="flex items-center gap-2 text-sm text-emerald-300">
                  <Tag className="w-4 h-4" /> {coupon.code} — {coupon.percent}% off
                </span>
                <button onClick={() => setCoupon(null)} data-testid="cart-coupon-remove" className="text-slate-500 hover:text-rose-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  placeholder="DESYNC10"
                  data-testid="cart-coupon-input"
                  className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] text-sm h-10 font-mono uppercase"
                />
                <button
                  onClick={applyCoupon}
                  disabled={checking || !couponInput.trim()}
                  data-testid="cart-coupon-apply"
                  className="shrink-0 px-4 rounded-lg border border-[#2E6BFF]/40 text-[#8FB8E8] text-sm font-medium hover:bg-[#2E6BFF]/10 disabled:opacity-40 transition-all"
                >
                  {checking ? "..." : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Currency</span>
              <CurrencySelect compact testid="cart-currency-select" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Subtotal</span>
              <span className="font-mono text-sm text-slate-300" data-testid="cart-subtotal">{fmt(total)}</span>
            </div>
            {bulkSavings > 0 && (
              <div className="flex items-center justify-between" data-testid="cart-bulk-discount-line">
                <span className="text-sm text-emerald-300">Bulk discount</span>
                <span className="font-mono text-sm text-emerald-300">-{fmt(bulkSavings)}</span>
              </div>
            )}
            {coupon && couponSavings > 0 && (
              <div className="flex items-center justify-between" data-testid="cart-discount-line">
                <span className="text-sm text-emerald-300">Discount ({coupon.code})</span>
                <span className="font-mono text-sm text-emerald-300">-{fmt(couponSavings)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1.5 border-t border-[#1E2D4A]">
              <span className="text-sm font-semibold text-white">Total</span>
              <span className="font-mono text-xl font-bold text-white" data-testid="cart-total">{fmt(payable)}</span>
            </div>
            {currency !== "aud" && (
              <p className="text-[10px] text-slate-500 text-right" data-testid="cart-charge-note">
                Card &amp; crypto charge in {currency.toUpperCase()} · Bank transfer charges in AUD
              </p>
            )}
          </div>

          <div>
            <label className="text-sm text-slate-300 block mb-2">
              Delivery email — your keys land here
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="cart-email-input"
              className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] text-sm h-11"
            />
          </div>

          {discordRequired ? (
            <div data-testid="discord-link-block">
              <label className="text-sm text-slate-300 block mb-2">
                Discord account — you get your customer role automatically after payment
              </label>
              {discordLink ? (
                <div className="flex items-center justify-between gap-3 px-4 h-11 rounded-md border border-emerald-500/40 bg-emerald-500/10" data-testid="discord-linked-badge">
                  <span className="text-sm text-emerald-300 truncate">Linked as {discordLink.name}</span>
                  <button onClick={unlinkDiscord} data-testid="discord-unlink" className="text-[11px] text-slate-400 hover:text-white shrink-0">
                    change
                  </button>
                </div>
              ) : (
                <button
                  onClick={startDiscordLink}
                  data-testid="discord-link-button"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 h-11 rounded-md bg-[#5865F2] hover:bg-[#4752c4] text-white text-sm font-semibold transition-colors"
                >
                  Link your Discord account
                </button>
              )}
              <p className="text-[11px] text-slate-500 mt-1.5">
                Not in our server yet? You'll join it automatically when you pay.
              </p>
            </div>
          ) : (
            <div>
              <label className="text-sm text-slate-300 block mb-2">
                Discord username — for support & delivery
              </label>
              <Input
                value={discordUser}
                onChange={(e) => {
                  setDiscordUser(e.target.value);
                  localStorage.setItem("void_discord", e.target.value);
                }}
                placeholder="e.g. desyncuser"
                data-testid="cart-discord-input"
                className="bg-[#050B18] border-[#1E2D4A] focus-visible:ring-[#2E6BFF] text-sm h-11"
              />
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="terms-agree-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="terms-agree-checkbox"
              className="mt-0.5 w-4 h-4 shrink-0 accent-[#2E6BFF]"
            />
            <span className="text-xs text-slate-400 leading-relaxed">
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="terms-agree-link" className="text-[#7FB0FF] hover:text-white underline underline-offset-2 transition-colors">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="privacy-agree-link" className="text-[#7FB0FF] hover:text-white underline underline-offset-2 transition-colors">
                Privacy Policy
              </a>
              . All sales are final except where required by law.
            </span>
          </label>

          <button
            onClick={checkout}
            disabled={loading || items.length === 0 || !agreed || !discordReady || (!discordRequired && !discordUser.trim())}
            data-testid="cart-checkout-button"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold shadow-[0_8px_24px_rgba(46,107,255,0.35)] disabled:opacity-40 disabled:pointer-events-none transition-all duration-200 active:scale-95"
          >
            <Lock className="w-4 h-4" />
            {loading ? "Redirecting to Stripe..." : "Pay with card"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>

          <button
            onClick={bankCheckout}
            disabled={loading || items.length === 0 || !agreed || !discordReady || (!discordRequired && !discordUser.trim())}
            data-testid="cart-bank-transfer-button"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#2E6BFF]/40 text-[#8FB8E8] text-sm font-semibold hover:bg-[#2E6BFF]/10 disabled:opacity-40 disabled:pointer-events-none transition-all duration-200"
          >
            <Landmark className="w-4 h-4" />
            {loading ? "Reserving..." : "Bank Transfer (PayID / BSB)"}
          </button>
          <p className="text-center text-[11px] text-slate-500 -mt-1" data-testid="bank-transfer-note">
            Bank transfer is manually confirmed — not instant delivery
          </p>

          {cryptoCoins.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="crypto-coin-picker">
                {cryptoCoins.map((c) => {
                  const dup = cryptoCoins.filter((x) => x.symbol === c.symbol).length > 1;
                  const label = dup ? `${c.symbol}·${c.short_net}` : c.symbol;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setCoin(c.id)}
                      data-testid={`crypto-coin-${c.id}`}
                      title={`${c.name} — ${c.network}`}
                      className={`flex-1 min-w-[70px] px-2 py-2 rounded-lg border text-[11px] font-mono font-bold uppercase tracking-widest transition-all ${
                        coin === c.id
                          ? "border-[#F7931A] bg-[#F7931A]/15 text-[#F5B45E]"
                          : "border-[#1E2D4A] text-slate-500 hover:border-[#F7931A]/40"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={cryptoCheckout}
                disabled={loading || items.length === 0 || !agreed || !discordReady || (!discordRequired && !discordUser.trim()) || !coin}
                data-testid="cart-crypto-button"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-[#F7931A]/40 text-[#F5B45E] text-sm font-semibold hover:bg-[#F7931A]/10 disabled:opacity-40 disabled:pointer-events-none transition-all duration-200"
              >
                <Bitcoin className="w-4 h-4" />
                {loading ? "Creating invoice..." : `Pay with ${(cryptoCoins.find((c) => c.id === coin) || {}).symbol || "Crypto"}`}
              </button>
              <p className="text-center text-[11px] text-slate-500 -mt-1" data-testid="crypto-note">
                {(cryptoCoins.find((c) => c.id === coin) || {}).network
                  ? `Send ${(cryptoCoins.find((c) => c.id === coin) || {}).symbol} on ${(cryptoCoins.find((c) => c.id === coin) || {}).network} — delivers automatically once confirmed`
                  : "Crypto delivers automatically once the network confirms — usually a few minutes"}
              </p>
            </>
          )}

          {SHOW_PAYPAL && PAYPAL_CLIENT_ID && (
            <div data-testid="paypal-section">
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <div className="flex-1 h-px bg-[#1E2D4A]" />
                <span>or</span>
                <div className="flex-1 h-px bg-[#1E2D4A]" />
              </div>
              <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "AUD" }}>
                <div data-testid="paypal-buttons" className="min-h-[45px]">
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 45 }}
                    disabled={items.length === 0}
                    forceReRender={[payable, email]}
                    createOrder={async () => {
                      if (!validEmail) {
                        toast.error("Enter your email first — keys are delivered there");
                        throw new Error("email required");
                      }
                      localStorage.setItem("void_email", email);
                      const { data } = await api.post("/paypal/create", cartPayload());
                      return data.paypal_order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await api.post("/paypal/capture", { paypal_order_id: data.orderID });
                        window.location.href = `/payment/success?order=${res.data.order_id}`;
                      } catch (e) {
                        toast.error(apiError(e));
                      }
                    }}
                    onError={() => toast.error("PayPal hit an error — try again or pay by card")}
                    onCancel={() => toast.info("PayPal checkout cancelled")}
                  />
                </div>
              </PayPalScriptProvider>
            </div>
          )}

          <p className="text-xs text-slate-600 text-center">
            Keys are emailed to you instantly after payment
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
